<script setup lang="ts">
import { resolveNavigationTarget } from "#bb/lib/navigationTarget";
import { eventBus } from "#bb/lib/eventBus";
import { resolveCandidateReportCachePath } from "#bb/lib/pathContract";

const props = defineProps<{ id: string }>();
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});
const features = inject("render-features", {
  editor: false,
  resourceDefinitionPath: null as string | null,
});
const route = useRoute();
const { siteId } = useSite();
const { profile } = useProfile();
const logger = useLogger("Redirect");
let redirectTimeout: ReturnType<typeof setTimeout> | null = null;

const commands = [
  {
    type: "group",
    id: "editors-group",
    name: "Editors",
    description: "Redirect editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the redirect target and delay",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Redirect",
    icon: "lucide:box-select",
  },
});

const shouldCheckReportData = computed(
  () =>
    definitionData.value?.checkReportData ??
    Boolean(definitionData.value?.alternatePath || definitionData.value?.automationPath)
);

const shouldIncludeUserId = computed(
  () => definitionData.value?.includeUserId ?? shouldCheckReportData.value
);

const userQuery = computed(() =>
  shouldIncludeUserId.value
    ? {
        user: profile.value?.id,
      }
    : undefined
);

const reportCachePath = computed(() => {
  const value = definitionData.value?.reportFileName;
  return typeof value === "string" && value.trim().length ? value.trim() : "report";
});

const { resolveHref } = useAppRoute();

const primaryNavigationTarget = computed(() =>
  resolveNavigationTarget(definitionData.value?.reportPath, {
    currentPath: route.fullPath,
    resourceDefinitionPath: features.resourceDefinitionPath,
    query: userQuery.value,
    resolvePath: resolveHref,
  })
);

const alternateNavigationTarget = computed(() =>
  resolveNavigationTarget(
    definitionData.value?.alternatePath ?? definitionData.value?.automationPath,
    {
      currentPath: route.fullPath,
      resourceDefinitionPath: features.resourceDefinitionPath,
      query: userQuery.value,
      resolvePath: resolveHref,
    }
  )
);

const resolveRedirectTarget = async () => {
  if (!shouldCheckReportData.value) {
    return primaryNavigationTarget.value;
  }

  const resolvedSiteId = siteId.value;
  const userId = profile.value?.id;

  if (!resolvedSiteId) {
    return primaryNavigationTarget.value;
  }
  if (!userId) {
    throw new Error(
      "Redirect report cache check requires a candidate userId to resolve the configured report path",
    );
  }

  const { get } = useFileEntries(siteId);
  const filePath = resolveCandidateReportCachePath(
    reportCachePath.value,
    userId,
  );
  const fileEntry = await get(filePath, "metadata", {
    suppressNotFoundToast: true,
  });

  return fileEntry ? primaryNavigationTarget.value : alternateNavigationTarget.value ?? primaryNavigationTarget.value;
};

onMounted(async () => {
  if (features.editor) {
    // Don't redirect in editor mode
    return;
  }
  
  const delay = (definitionData.value?.delaySeconds ?? 0) * 1000;

  redirectTimeout = setTimeout(async () => {
    let navigationTarget;
    try {
      navigationTarget = await resolveRedirectTarget();
    } catch (error) {
      // Report-cache resolution can fail (e.g. the candidate profile has not
      // loaded yet). Falling through to the primary target keeps the redirect
      // working instead of leaving the candidate stuck on this page with an
      // unhandled rejection and no navigation at all.
      logger.error(
        error instanceof Error
          ? error.message
          : "Failed to resolve the redirect target",
      );
      navigationTarget = primaryNavigationTarget.value;
    }

    if (navigationTarget?.href) {
      await navigateTo(navigationTarget.href, {
        external: navigationTarget.external,
      });
    }
  }, delay);
});

onBeforeUnmount(() => {
  if (redirectTimeout) {
    clearTimeout(redirectTimeout);
  }
});
</script>

<template />

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.REDIRECT,
  id: "redirect",
  icon: "lucide:box-select",
  type: "Redirect",
  description: "Redirects a user to another page or external destination, optionally based on report data.",
  editor: () => import("#bb/components/editor/navigation/redirect.vue"),
  default: {
    reportPath: "/",
    alternatePath: "",
    checkReportData: false,
    includeUserId: false,
    reportFileName: "report",
    delaySeconds: 0,
  },
  categories: ["Navigation"],
  order: 12004,
};
</script>
